/**
 * Bundle Builder — catalog → holding basket → tier progress → add to cart.
 */
if (!customElements.get("bundle-builder")) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
        this.unitPrice = Number(this.dataset.unitPrice || 0);
        this.maxQty = Number(this.dataset.maxTierQty || 1);
        this.strings = window.bundleBuilderStrings || {};

        this.progressScope = this.querySelector("[data-bundle-progress-scope]");

        try {
          this.tiers = JSON.parse(
            this.progressScope?.querySelector("[data-bundle-tiers]")
              ?.textContent || "[]",
          );
        } catch (e) {
          this.tiers = [];
        }

        if (this.progressScope && window.BundleProgress) {
          this.progressController = new window.BundleProgress(
            this.progressScope.querySelector(".bundle-progress__layout") ||
              this.progressScope,
            { tiers: this.tiers, count: 0 },
          );
        }

        this.holding = [];
        this.holdingGrid = this.querySelector("[data-holding-grid]");
        this.holdingScroll = this.querySelector("[data-holding-scroll]");
        this.submitButton = this.querySelector("[data-real-add-to-cart]");
        this.maxTierNotice = this.querySelector("[data-max-tier-notice]");
        this.pricingRow = this.querySelector("[data-pricing-row]");
        this.priceCompareEl = this.querySelector("[data-price-compare]");
        this.priceSaleEl = this.querySelector("[data-price-sale]");
        this.savingsBadgeEl = this.querySelector("[data-savings-badge]");
        this.errorEl = this.querySelector("[data-error-message]");
        this.errorTextEl = this.querySelector("[data-error-text]");
        this.stickyPanel = this.querySelector("[data-sticky-panel]");
        this.panelToggle = this.querySelector("[data-panel-toggle]");
        this.pairsCountEl = this.querySelector("[data-pairs-count]");
        this.pairsPerPairEl = this.querySelector("[data-pairs-per-pair]");

        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onRealAddToCart = this.onRealAddToCart.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPanelToggle = this.onPanelToggle.bind(this);

        this.addEventListener("click", this.onClick);
        this.addEventListener("submit", this.onSubmit, true);
        this.submitButton?.addEventListener("click", this.onRealAddToCart);
        this.panelToggle?.addEventListener("click", this.onPanelToggle);
        window.addEventListener("resize", this.onResize);

        this.syncPanelCollapsedForViewport();
        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
        this.removeEventListener("submit", this.onSubmit, true);
        this.submitButton?.removeEventListener("click", this.onRealAddToCart);
        this.panelToggle?.removeEventListener("click", this.onPanelToggle);
        window.removeEventListener("resize", this.onResize);
      }

      onResize() {
        this.syncPanelCollapsedForViewport();
        this.renderProgress();
        this.updateHoldingScroll();
      }

      isMobileViewport() {
        return window.matchMedia("(max-width: 749px)").matches;
      }

      syncPanelCollapsedForViewport() {
        if (!this.stickyPanel) return;

        if (!this.isMobileViewport()) {
          this.stickyPanel.classList.remove("bundle-builder__panel--collapsed");
          this.updatePanelToggleState(false);
          return;
        }

        if (
          !this.stickyPanel.classList.contains(
            "bundle-builder__panel--collapsed",
          )
        ) {
          // Keep the user's expanded choice on mobile until they collapse again.
          this.updatePanelToggleState(true);
          return;
        }

        this.updatePanelToggleState(false);
      }

      onPanelToggle(event) {
        event.preventDefault();
        if (!this.stickyPanel || !this.isMobileViewport()) return;

        const surface = this.stickyPanel.querySelector(
          ".bundle-builder__panel-surface",
        );
        const startHeight = surface?.offsetHeight ?? 0;
        const collapsed = this.stickyPanel.classList.toggle(
          "bundle-builder__panel--collapsed",
        );
        this.updatePanelToggleState(!collapsed);

        if (!surface || startHeight <= 0) return;

        const endHeight = surface.offsetHeight;
        if (startHeight === endHeight) return;

        surface.style.height = `${startHeight}px`;
        surface.offsetHeight;
        surface.style.transition = "height 0.35s ease";
        surface.style.height = `${endHeight}px`;

        let cleaned = false;
        const cleanup = (transitionEvent) => {
          if (
            transitionEvent &&
            (transitionEvent.target !== surface ||
              transitionEvent.propertyName !== "height")
          ) {
            return;
          }
          if (cleaned) return;
          cleaned = true;
          surface.style.height = "";
          surface.style.transition = "";
          surface.removeEventListener("transitionend", cleanup);
        };

        surface.addEventListener("transitionend", cleanup);
        setTimeout(cleanup, 400);
      }

      updatePanelToggleState(expanded) {
        if (!this.panelToggle) return;

        this.panelToggle.setAttribute(
          "aria-expanded",
          expanded ? "true" : "false",
        );
        this.panelToggle.setAttribute(
          "aria-label",
          expanded
            ? this.strings.collapsePanel || "Collapse bundle summary"
            : this.strings.expandPanel || "Expand bundle summary",
        );
      }

      onClick(event) {
        const addBtn = event.target.closest("[data-add-to-holding]");
        if (addBtn && this.contains(addBtn) && !addBtn.disabled) {
          event.preventDefault();
          this.addFromButton(addBtn);
          return;
        }

        const removeBtn = event.target.closest("[data-remove-from-holding]");
        if (removeBtn && this.contains(removeBtn)) {
          event.preventDefault();
          this.removeItem(Number(removeBtn.dataset.removeFromHolding));
        }
      }

      onSubmit(event) {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form || !this.contains(form)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const variantInput = form.querySelector('[name="id"]');
        const variantId = variantInput?.value;
        if (!variantId || variantInput.disabled) return;

        const modal = form.closest("quick-add-modal");
        let card = form.closest("[data-product-card]");

        if (!card && modal) {
          const cardId = modal.id.replace("QuickAdd-Bundle-", "");
          card = this.querySelector(`[data-card-id="${cardId}"]`);
        }

        const infoRoot = form.closest(".quick-add-modal__content-info");
        const modalTitle = infoRoot
          ?.querySelector(".product__title, h1, h2")
          ?.textContent?.trim();
        const modalImage = infoRoot?.querySelector(
          ".product__media img, img",
        )?.src;

        this.addItem({
          variantId: String(variantId),
          productId: card?.dataset.productId || "",
          title: modalTitle || card?.dataset.title || "",
          image: modalImage || card?.dataset.image || null,
          price: Number(card?.dataset.price || 0),
        });

        if (modal) {
          const closeBtn = modal.querySelector(".quick-add-modal__toggle");
          closeBtn?.click();
        }
      }

      addFromButton(button) {
        this.addItem({
          variantId: String(button.dataset.variantId),
          productId: button.dataset.productId || "",
          title: button.dataset.title || "",
          image: button.dataset.image || null,
          price: Number(button.dataset.price || 0),
        });
      }

      addItem(item) {
        if (!item.variantId) return;

        const maxQty = this.getMaxAllowedQty();
        if (this.holding.length >= maxQty) {
          this.showError(
            this.strings.holdingFull ||
              "You've reached the maximum number of items for this bundle.",
          );
          return;
        }

        this.showError("");
        this.holding.push({
          ...item,
          key: `${item.variantId}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        });
        this.render();
      }

      removeItem(index) {
        if (index < 0 || index >= this.holding.length) return;
        this.holding.splice(index, 1);
        this.showError("");
        this.render();
      }

      getMaxAllowedQty() {
        if (!this.tiers.length) return this.maxQty || 99;
        return Math.max(...this.tiers.map((tier) => Number(tier.qty || 0)));
      }

      getActiveTier() {
        const count = this.holding.length;
        let active = null;
        for (const tier of this.tiers) {
          if (count >= Number(tier.qty)) active = tier;
        }
        return active;
      }

      getTierDiscountCents(tier, compareTotal) {
        if (!tier || compareTotal <= 0) return 0;

        const discountType = tier.discountType || "fixed";
        if (discountType === "percentage") {
          const percent = Number(tier.discountPercent || 0);
          if (percent <= 0) return 0;
          return Math.round(compareTotal * (percent / 100));
        }

        return Math.max(0, Number(tier.discountFixed || 0));
      }

      getSaleTotal(compareTotal) {
        const count = this.holding.length;
        if (count <= 0) return 0;

        const activeTier = this.getActiveTier();
        if (!activeTier) return compareTotal;

        const discount = this.getTierDiscountCents(activeTier, compareTotal);
        if (discount <= 0) return compareTotal;

        return Math.max(0, compareTotal - discount);
      }

      hasReachedMaxTier() {
        const maxQty = this.getMaxAllowedQty();
        return maxQty > 0 && this.holding.length >= maxQty;
      }

      canCheckout() {
        return this.holding.length > 0;
      }

      render() {
        this.renderHolding();
        this.renderProgress();
        this.updatePricing();
        this.updatePairsCount();
        this.updateMaxTierNotice();
        this.updatePickButtons();
        this.updateSubmitState();
      }

      updatePickButtons() {
        const atMax = this.hasReachedMaxTier();
        this.querySelectorAll("[data-bundle-pick]").forEach((btn) => {
          const available = btn.dataset.available === "true";
          const disabled = !available || atMax;
          btn.disabled = disabled;
          btn.setAttribute("aria-disabled", disabled ? "true" : "false");
        });
      }

      renderHolding() {
        if (!this.holdingGrid) return;

        const removeLabel = this.strings.removeItem || "Remove";

        this.holdingGrid.innerHTML = this.holding
          .map((item, index) => {
            const img = item.image
              ? `<img class="bundle-builder__product-image" src="${this.escapeAttr(item.image)}" alt="" width="300" height="400" loading="lazy">`
              : `<span class="bundle-builder__product-image bundle-builder__product-image--placeholder" aria-hidden="true"></span>`;
            return `<div class="bundle-builder__holding-item" data-holding-index="${index}">
              <div class="bundle-builder__product-card bundle-builder__product-card--holding">
                <div class="bundle-builder__product-media">
                  ${img}
                  <span class="bundle-builder__product-media-fade" aria-hidden="true"></span>
                  <span class="bundle-builder__product-name">${this.escapeHtml(item.title || "")}</span>
                  <button type="button" class="bundle-builder__holding-remove bundle-builder__icon-btn" data-remove-from-holding="${index}" aria-label="${this.escapeAttr(removeLabel)}">
                    ${this.removeIconSvg()}
                  </button>
                </div>
              </div>
            </div>`;
          })
          .join("");

        this.holdingGrid.querySelectorAll("img").forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", () => this.updateHoldingScroll(), {
              once: true,
            });
          }
        });

        this.updateHoldingScroll();
      }

      updateHoldingScroll() {
        if (!this.holdingScroll || !this.holdingGrid) return;

        requestAnimationFrame(() => {
          const scroll = this.holdingScroll;
          const firstItem = this.holdingGrid.querySelector(
            ".bundle-builder__holding-item",
          );

          if (!firstItem) {
            scroll.style.maxHeight = "";
            scroll.classList.remove("is-scrollable");
            return;
          }

          const itemHeight = firstItem.getBoundingClientRect().height;
          const gridStyles = getComputedStyle(this.holdingGrid);
          const gap =
            parseFloat(gridStyles.rowGap || gridStyles.gap || "0") || 0;
          const visibleRows = 1.25;
          const rowGaps = 1;

          if (itemHeight > 0) {
            scroll.style.maxHeight = `${visibleRows * itemHeight + rowGaps * gap}px`;
          }

          const scrollable = scroll.scrollHeight > scroll.clientHeight + 1;
          scroll.classList.toggle("is-scrollable", scrollable);
        });
      }

      renderProgress() {
        this.progressController?.setCount(this.holding.length);
      }

      updatePricing() {
        if (!this.priceSaleEl || !this.pricingRow) return;

        if (this.holding.length === 0) {
          this.pricingRow.hidden = true;
          this.priceSaleEl.textContent = "";
          if (this.priceCompareEl) this.priceCompareEl.hidden = true;
          if (this.savingsBadgeEl) this.savingsBadgeEl.hidden = true;
          return;
        }

        const compareTotal = this.holding.reduce(
          (sum, item) => sum + Number(item.price || 0),
          0,
        );
        const saleTotal = this.getSaleTotal(compareTotal);
        const savings = compareTotal - saleTotal;

        this.priceSaleEl.textContent = this.formatMoney(saleTotal);
        this.pricingRow.hidden = false;

        if (savings > 0 && this.priceCompareEl && this.savingsBadgeEl) {
          this.priceCompareEl.textContent = this.formatMoney(compareTotal);
          this.priceCompareEl.hidden = false;
          const saveLabel = this.strings.saveAmount || "Save {{ amount }}";
          this.savingsBadgeEl.textContent = saveLabel.replace(
            /\{\{\s*amount\s*\}\}/g,
            this.formatMoney(savings),
          );
          this.savingsBadgeEl.hidden = false;
        } else {
          if (this.priceCompareEl) this.priceCompareEl.hidden = true;
          if (this.savingsBadgeEl) this.savingsBadgeEl.hidden = true;
        }
      }

      updatePairsCount() {
        if (!this.pairsCountEl) return;

        const count = this.holding.length;
        const template = this.strings.qtyPairs || "{{ count }} pairs";
        this.pairsCountEl.textContent = template.replace(
          /\{\{\s*count\s*\}\}/g,
          String(count),
        );

        if (!this.pairsPerPairEl) return;

        if (count <= 0) {
          this.pairsPerPairEl.textContent = "";
          this.pairsPerPairEl.hidden = true;
          return;
        }

        const compareTotal = this.holding.reduce(
          (sum, item) => sum + Number(item.price || 0),
          0,
        );
        const saleTotal = this.getSaleTotal(compareTotal);
        const perPair = Math.round(saleTotal / count);
        const perPairTemplate = this.strings.pricePerPair || "{{ price }}/pair";
        this.pairsPerPairEl.textContent = perPairTemplate.replace(
          /\{\{\s*price\s*\}\}/g,
          this.formatMoney(perPair),
        );
        this.pairsPerPairEl.hidden = false;
      }

      formatMoney(cents) {
        const value = (Number(cents) / 100).toFixed(2);
        const format = this.moneyFormat || "${{amount}}";
        const withComma = value.replace(".", ",");
        const noDecimals = String(Math.round(Number(cents) / 100));
        return format
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount\s*\}\}/g, value);
      }

      updateMaxTierNotice() {
        if (!this.maxTierNotice) return;
        const show = this.hasReachedMaxTier();
        this.maxTierNotice.hidden = !show;
      }

      updateSubmitState() {
        if (!this.submitButton) return;
        const ready = this.canCheckout();
        this.submitButton.disabled = !ready;
        this.submitButton.setAttribute(
          "aria-disabled",
          ready ? "false" : "true",
        );
      }

      showError(message) {
        if (!this.errorEl || !this.errorTextEl) return;
        this.errorTextEl.textContent = message || "";
        this.errorEl.hidden = !message;
      }

      async onRealAddToCart(event) {
        event.preventDefault();

        if (!this.canCheckout() || this.submitButton?.disabled) {
          return;
        }

        this.showError("");
        const tier = this.getActiveTier();
        const compareTotal = this.holding.reduce(
          (sum, item) => sum + Number(item.price || 0),
          0,
        );
        const saleTotal = this.getSaleTotal(compareTotal);
        await this.addToCart(tier, saleTotal);
      }

      async addToCart(tier, saleTotal) {
        const spinner = this.submitButton?.querySelector(".loading__spinner");
        const cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        const itemsMap = new Map();
        this.holding.forEach((item) => {
          const id = Number(item.variantId);
          if (!id) return;
          itemsMap.set(id, (itemsMap.get(id) || 0) + 1);
        });

        const items = Array.from(itemsMap.entries()).map(([id, quantity]) => {
          const lineItem = { id, quantity };
          if (tier) {
            lineItem.properties = {
              _bundle_tier: String(tier.qty),
              _bundle_discount_type: tier.discountType || "fixed",
              _bundle_discount_fixed: String(tier.discountFixed || 0),
              _bundle_discount_percent: String(tier.discountPercent || 0),
              _bundle_final_price: String(saleTotal || 0),
              _bundle_quantity: String(this.holding.length),
            };
          }
          return lineItem;
        });

        if (!items.length) return;

        this.submitButton?.setAttribute("aria-disabled", "true");
        this.submitButton?.classList.add("loading");
        this.submitButton.disabled = true;
        spinner?.classList.remove("hidden");

        const body = { items };
        if (cart?.getSectionsToRender) {
          body.sections = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(",");
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            this.showError(data.description || data.message || "Error");
            return;
          }

          const matchedTier = tier;
          this.holding = [];
          this.render();

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish?.(PUB_SUB_EVENTS.cartUpdate, {
            source: "bundle-builder",
            cartData: data,
          });

          const payload = await this.normalizeCartAddResponse(
            data,
            cart,
            matchedTier,
          );
          this.renderCartContents(cart, payload);
          cart.classList.remove("is-empty");
        } catch (error) {
          console.error(error);
          this.showError(error.message || "Error");
        } finally {
          this.submitButton?.classList.remove("loading");
          spinner?.classList.add("hidden");
          this.updateSubmitState();
        }
      }

      async normalizeCartAddResponse(data, cart, tier) {
        const items = Array.isArray(data.items)
          ? data.items
          : data.key
            ? [data]
            : [];
        const added = items[items.length - 1] || data;
        let sections = data.sections;

        const hasSections = sections && Object.keys(sections).length > 0;
        if (!hasSections && cart?.getSectionsToRender) {
          const ids = cart.getSectionsToRender().map((section) => section.id);
          try {
            const root = window.shopUrl || window.location.origin;
            const response = await fetch(`${root}/?sections=${ids.join(",")}`);
            sections = await response.json();
          } catch (error) {
            console.error(error);
            sections = {};
          }
        }

        const matchedTier = tier || this.getActiveTier();

        return {
          ...data,
          key: data.key || added.key,
          itemKeys: items.map((item) => item.key).filter(Boolean),
          bundleTitle: matchedTier?.label || "",
          sections: sections || {},
          id: data.id || added.id,
        };
      }

      renderCartContents(cart, payload) {
        if (typeof cart.renderContents !== "function") {
          window.location = window.routes.cart_url;
          return;
        }

        try {
          cart.renderContents(payload);
        } catch (error) {
          console.error(error);
          cart.open?.();
        }
      }

      removeIconSvg() {
        return `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"/><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"/></svg>`;
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      }

      escapeAttr(value) {
        return this.escapeHtml(value).replace(/'/g, "&#39;");
      }
    },
  );
}
